import { Component } from "react";
import { Form } from "semantic-ui-react";

import RuvixxForm from "components/RuvixxForm";
import CampaignTargetCallService from "services/CampaignTargetCall";
import CampaignService from "services/Campaign";
import ContactService from "services/Contact";
import UserService from "services/User";
import { isUserActive, formatUserName } from "helpers/user";
import { TagFormField } from "components/TagFormField";
import tagConstants from "constants/Tag";

const CALL_DIRECTION_INBOUND = "INBOUND";

class EditCallForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      call: this.props.call,
      callId: this.props.call.id,
      selectedContact: this.props.call.contact_id,
      campaignId: this.props.call.campaign_id,
      entityId: this.props.call.entity_id,
      dispositionId: this.props.call.disposition_id,
      statusVerified: this.props.call.status_verified || false,
      isInboundCall: this.props.call.call_direction === CALL_DIRECTION_INBOUND,
      selectedCampaign: this.props.call.campaign_id || null,
      selectedCaller: this.props.call.agent_id || null,
      tags: this.props.call?.tags || [],
      contactOptions: [],
    };

    this.onSuccess = this.onSuccess.bind(this);
  }

  handleChange = (e, data) => {
    this.setState({ [data.name]: data.value });
  };

  toggle = (e, data) => this.setState({ [data.name]: data.checked });

  handleTagChange = nextTags => this.setState({ tags: nextTags });

  handleSubmit = async () => {
    const {
      callId,
      dispositionId,
      statusVerified,
      selectedCampaign,
      selectedCaller,
      selectedContact,
      tags,
    } = this.state;

    const call = await CampaignTargetCallService.editCall({
      callId: callId,
      dispositionId: dispositionId,
      statusVerified: statusVerified,
      campaignId: selectedCampaign,
      contactId: selectedContact,
      creatorUserId: selectedCaller,
      tags,
    });

    // Bulk-link all other unlinked inbound calls with the same phone number
    if (
      this.state.isInboundCall &&
      selectedContact &&
      this.props.call.info?.from
    ) {
      try {
        await CampaignTargetCallService.linkContactToCalls({
          externalNumber: this.props.call.info.from,
          contactId: selectedContact,
        });
      } catch (e) {
        console.warn("Failed to bulk-link unlinked calls:", e);
      }
    }

    this.setState({ call });
  };

  onSuccess() {
    this.props.handleClose();
    this.props.onSuccess(this.state.call);
  }

  async fetchCampaigns() {
    const campaigns = await CampaignService.getCampaignsForFilters();
    this.setState({ campaigns });
  }

  fetchUsers = async () => {
    const rawUsers = await UserService.getUsers();
    const { selectedCaller } = this.state;

    // Filter to active users + currently selected agent (even if deactivated)
    const users = rawUsers
      .filter(user => isUserActive(user) || user.id === selectedCaller)
      .map(user => ({
        id: user.id,
        name: formatUserName(user.full_name, user.status),
        email: user.email,
      }));

    this.setState({ users }, this.updateFilterOptions);
  };

  componentDidMount() {
    this.fetchCampaigns();
    this.fetchUsers();
    this.updateContactOptions();
  }

  updateContactOptions = async searchQuery => {
    const filteredContacts = await ContactService.getContactsForFilters({
      phone_number: this.props.call.info.from,
      search_query: searchQuery,
    });

    const contactOptions = filteredContacts.map(({ id, name }) => ({
      key: id,
      value: id,
      text: name,
    }));

    const currentContact = filteredContacts.find(
      contact => contact.id === this.props.call.contact_id
    );

    if (!currentContact && this.props.call.contact_id) {
      contactOptions.push({
        key: this.props.call.contact_id,
        value: this.props.call.contact_id,
        text: this.props.call.contact_full_name,
      });
    }

    this.setState({
      contactOptions,
    });
  };

  checkForm = () => {
    if (this.state.isInboundCall) {
      return (
        this.state.dispositionId &&
        this.state.selectedContact &&
        this.state.selectedCampaign &&
        this.state.selectedCaller
      );
    } else {
      return !!this.state.callId && !!this.state.dispositionId;
    }
  };

  render() {
    const { dispositionId, isInboundCall, campaigns, users } = this.state;

    return (
      <RuvixxForm
        ready={this.checkForm()}
        onSubmit={this.handleSubmit}
        onSuccess={this.onSuccess}
      >
        <Form.Select
          selectOnBlur={false}
          required
          label="Disposition"
          name="dispositionId"
          onChange={this.handleChange}
          selection
          search
          options={this.props.callStatuses}
          value={dispositionId}
        />
        {isInboundCall && (
          <Form.Select
            selectOnBlur={false}
            required
            label="Contact"
            placeholder="Select contact or type to search"
            search
            options={this.state.contactOptions}
            name="selectedContact"
            value={this.state.selectedContact}
            onChange={this.handleChange}
          />
        )}
        {isInboundCall && campaigns && users && (
          <>
            <Form.Select
              selectOnBlur={false}
              required
              label="Campaign"
              name="selectedCampaign"
              onChange={this.handleChange}
              selection
              search
              options={campaigns.map(campaign => ({
                key: campaign.id,
                text: campaign.name,
                value: campaign.id,
              }))}
              value={this.state.selectedCampaign}
            />
            <Form.Select
              selectOnBlur={false}
              required
              label="Agent"
              name="selectedCaller"
              onChange={this.handleChange}
              selection
              search
              options={users.map(user => ({
                key: user.id,
                text: user.name,
                value: user.id,
              }))}
              value={this.state.selectedCaller}
            />
          </>
        )}
        <TagFormField
          modelType={tagConstants.taggableModels.campaign_target_call}
          onChange={this.handleTagChange}
          formValue={this.state.tags}
        />
      </RuvixxForm>
    );
  }
}

export default EditCallForm;
